// Report-param grammar for a matrix-family question -- the block itself, not one of its batches.
//
// Four params carry the block's state, addressed by prefix:
//   tcv  which view its sub-questions are charted in, plus the labels or the tops that view reads
//   cm   the chart's mode -- or the marker saying the word cloud is showing instead of a chart
//   z    the chart's zoom
//   s    the sort order
//
// `cm` is overloaded on purpose, the way the coffee views left it: a question showing its answer
// variants as a word cloud has no chart mode to remember, so the one param says which of the two is
// on screen. `off` is the marker for a question that offers the cloud by default and was turned back.
//
// The grammar was split across three coffee views -- the block owned the view and the cloud marker,
// the chart view the mode and the zoom, the shared items list the sort order. They are one bag here.

import { DEFAULT_CHART_LABELS, DEFAULT_CHART_TOPS } from '@aytm/app/stats/page/question-views/components/lib/constants'
import { getDefaultChartView } from '@aytm/app/stats/page/question-views/components/controls/lib/view-modes'
import { getDefaultChartMode } from '@aytm/app/stats/page/question-views/mx/components/chart/lib/chartModes'
import {
  DEFAULT_SORT_ORDER,
  decodeSortParam,
  encodeSortParam
} from '@aytm/app/stats/page/question-views/components/lib/sort-param'
import { WC_CHART_MODE, isWcChartMode } from '@aytm/app/stats/page/question-views/components/lib/chart-modes'

export { DEFAULT_SORT_ORDER }

export { WC_CHART_MODE, isWcChartMode }

// the views that read a score rather than an answer distribution, and so name nothing to label
export const SCORE_VIEW_MODES = ['score', 'meanScore', 'boxScore']

// a ranking question with this many respondents is drawn by position rather than by entity
const POSITION_LABELS_THRESHOLD = 20

const RANKING_TYPES = ['ro', 'aro']

const PIE_CHART_MODE = 'pie'

export const resolveSortOrder = (section, defaultSortOrder = DEFAULT_SORT_ORDER) =>
  decodeSortParam(section, { defaultSortOrder })

export const calcSortParamsSection = (sortOrder, defaultSortOrder = DEFAULT_SORT_ORDER) => ({
  s: encodeSortParam(sortOrder, { defaultSortOrder })
})

// A block opens with every batch closed, so an empty list is what the param says nothing about.
export const resolveUncollapsed = section => (null == section ? '' : String(section))

export const calcCollapseSection = uncollapsed => ({ cb: uncollapsed?.length ? uncollapsed : null })

// A question that offers the cloud by default shows it until the researcher says otherwise, and
// `off` is how saying otherwise is written down. The default is settled here, as the block opens,
// so that from then on the mode says outright which of the two is up.
export const resolveChartMode = ({ cm } = {}, format = {}) => {
  if (isWcChartMode(cm)) return WC_CHART_MODE
  if (null == cm && isWcChartMode(format?.chartMode)) return WC_CHART_MODE

  return cm || null
}

// The chart mode the question itself declares, which the chart falls back to before the report's.
// The cloud marker is not one: a question declaring it has no chart mode of its own to fall back to.
export const calcModelChartMode = (chartMode, format = {}) => {
  if (isWcChartMode(chartMode)) return WC_CHART_MODE
  if (isWcChartMode(format?.chartMode)) return null

  return format?.chartMode || null
}

// The labels a ranking question opens on: entities, until there are enough respondents that naming
// each of them says less than naming the positions they picked.
export const calcDefaultChartLabels = (type, total) =>
  RANKING_TYPES.includes(type) && POSITION_LABELS_THRESHOLD < total ? 'positions' : DEFAULT_CHART_LABELS

// Which view the block opens on, and what that view reads. A stored view the question no longer
// offers -- or a box score with no tops to read -- is not a view at all, so the block falls back to
// its defaults and says so, which is what makes the stored value worth rewriting.
export const resolveViewState = ({ tcv } = {}, { viewModes = [], topsModes = [], type, total } = {}) => {
  const defaultChartView = getDefaultChartView(viewModes)
  const defaultChartLabels = calcDefaultChartLabels(type, total)

  const defaults = {
    viewMode: defaultChartView,
    labelsMode: defaultChartLabels,
    topsMode: DEFAULT_CHART_TOPS,
    error: false
  }

  if (null == tcv) return defaults

  const parts = String(tcv).split('|')

  const viewMode = parts[0] || defaultChartView
  const labelsMode = SCORE_VIEW_MODES.includes(viewMode) ? defaultChartLabels : parts[1] || defaultChartLabels
  const topsMode = 'boxScore' === viewMode ? parts[1] || DEFAULT_CHART_TOPS : DEFAULT_CHART_TOPS

  const error = ('boxScore' === viewMode && !topsModes.length) || !viewModes.some(item => item.value === viewMode)

  if (error) return { ...defaults, error: true }

  return { viewMode, labelsMode, topsMode, error: false }
}

// What the view is worth storing: nothing at all while it is the one the question opens on, and
// only the parts that depart from it otherwise.
export const calcViewSection = ({ viewMode, labelsMode, topsMode, viewModes = [], type, total } = {}) => {
  const defaultChartView = getDefaultChartView(viewModes)
  const defaultChartLabels = calcDefaultChartLabels(type, total)

  if (viewMode === defaultChartView && labelsMode === defaultChartLabels && topsMode === DEFAULT_CHART_TOPS) {
    return { tcv: null }
  }

  const labels = labelsMode === defaultChartLabels ? '' : `|${labelsMode}`
  const tops = topsMode === DEFAULT_CHART_TOPS ? '' : `|${topsMode}`

  return { tcv: `${viewMode}${labels}${tops}` }
}

// Picking a view starts it over: the chart mode and the sort order the previous view was read in
// mean nothing to the new one. A box score keeps the zoom, since it is the only view switch that
// stays on the same axis.
export const calcViewModeReset = (viewMode, defaultSortOrder = DEFAULT_SORT_ORDER) => ({
  viewMode,
  chartMode: null,
  sortOrder: defaultSortOrder,
  ...('boxScore' === viewMode ? {} : { zoomLevel: null })
})

// A zoom that matches the one the whole report is set to is not the question's own, so the key
// drops out of the persisted set.
export { calcZoomSection } from '@aytm/app/stats/page/question-views/components/lib/zoom-param'

// A chart mode that says what the question already says is not worth storing -- except when the
// question says "cloud" and the researcher wants a chart, which is what there is no silence for. A
// pie has no axis to zoom, so picking one drops the zoom with it.
export const calcChartParamsSection = ({ chartMode, format, viewMode, answer, rType, globalChartMode } = {}) => {
  const stored = format?.chartMode || 'off'

  if (isWcChartMode(stored) && !isWcChartMode(chartMode)) return { cm: 'off' }

  const section = { cm: chartMode === stored ? null : chartMode || null }

  if (isWcChartMode(chartMode)) return section

  const resolved = getDefaultChartMode(
    chartMode,
    viewMode,
    answer,
    rType,
    globalChartMode,
    true === format?.percentsOnly
  )

  return PIE_CHART_MODE === resolved ? { ...section, z: null } : section
}
